import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gamepad2, HelpCircle, CheckCircle2, XCircle, ArrowRight, RotateCcw, Trophy, ChevronLeft, Loader2, Sparkles } from "lucide-react";

function scoreColor(pct) {
  if (pct >= 80) return "text-emerald-500";
  if (pct >= 50) return "text-amber-500";
  return "text-rose-500";
}

function scoreMessage(pct) {
  if (pct >= 80) return "Bravo, tu maîtrises ! 🎉";
  if (pct >= 50) return "Bien joué, continue à t'entraîner 💪";
  return "Pas grave, recommence pour progresser 🌱";
}

function ScoreRing({ score, total }) {
  const pct = total ? Math.round((score / total) * 100) : 0;
  const color = scoreColor(pct);
  return (
    <div className="relative h-28 w-28">
      <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
        <path className="text-slate-100" stroke="currentColor" strokeWidth="3.5" fill="none"
          d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31" />
        <path className={color} stroke="currentColor" strokeWidth="3.5" fill="none" strokeLinecap="round"
          strokeDasharray={`${pct}, 100`}
          d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-bold text-slate-900">{score}/{total}</span>
        <span className="text-[11px] font-bold text-slate-400">{pct}%</span>
      </div>
    </div>
  );
}

export default function EntrainementPage() {
  const [view, setView] = useState("list"); // list | quiz | result
  const [topics, setTopics] = useState(null);
  const [topic, setTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/quiz/overview").then(({ data }) => setTopics(data)).catch(() => setTopics([]));
  }, []);

  const startQuiz = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quiz/questions?competency_code=${t.competency_code}`);
      setTopic(t); setQuestions(data); setIdx(0); setPicked(null); setScore(0); setView("quiz");
    } finally { setLoading(false); }
  };

  const choose = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === questions[idx].answer_index) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= questions.length) { setView("result"); return; }
    setIdx((n) => n + 1); setPicked(null);
  };

  const backToList = () => { setView("list"); setTopic(null); setQuestions([]); };

  // ---------- LIST ----------
  if (view === "list") {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
        <p className="text-sm font-bold text-sky-500 uppercase tracking-wide">Espace entraînement</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1">M'entraîner</h1>
        <p className="text-slate-500 font-medium mt-1">Des QCM pour réviser chaque compétence. Choisis un thème et teste-toi !</p>

        {topics === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 text-sky-400 animate-spin" /></div>
        ) : topics.length === 0 ? (
          <div className="mt-8 rounded-3xl bg-white border border-slate-100 shadow-sm p-12 text-center">
            <Gamepad2 className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 font-medium mt-3">Aucun quiz disponible pour le moment.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-7">
            {topics.map((t) => (
              <button key={t.competency_code} onClick={() => startQuiz(t)} disabled={loading}
                data-testid={`quiz-topic-${t.competency_code}`}
                className="group text-left rounded-3xl bg-white border border-slate-100 shadow-sm p-6 hover:border-sky-300 hover:shadow-md transition-all disabled:opacity-60">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-2xl bg-sky-100 text-sky-600 font-display font-bold">{t.competency_code}</span>
                  <span className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{t.count} questions</span>
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mt-4">{t.short}</h3>
                <p className="text-sm text-slate-500 font-medium mt-1 line-clamp-2">{t.name}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-sky-600 group-hover:gap-2.5 transition-all">
                  <HelpCircle className="h-4 w-4" /> S'entraîner <ArrowRight className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- RESULT ----------
  if (view === "result") {
    const total = questions.length;
    const pct = total ? Math.round((score / total) * 100) : 0;
    const msg = scoreMessage(pct);
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-8 text-center" data-testid="quiz-result">
          <div className="inline-flex items-center gap-1.5 bg-sky-50 text-sky-600 px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wide">
            <Trophy className="h-3.5 w-3.5" /> {topic?.short}
          </div>
          <div className="flex justify-center my-6"><ScoreRing score={score} total={total} /></div>
          <p className="font-display text-xl font-semibold text-slate-900" data-testid="quiz-score">{msg}</p>
          <p className="text-slate-500 font-medium mt-1">Tu as obtenu {score} bonne{score > 1 ? "s" : ""} réponse{score > 1 ? "s" : ""} sur {total}.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <button onClick={() => startQuiz(topic)} data-testid="quiz-retry-btn"
              className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6 py-3 font-bold transition-colors">
              <RotateCcw className="h-4 w-4" /> Recommencer
            </button>
            <button onClick={backToList} data-testid="quiz-back-btn"
              className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-6 py-3 font-bold transition-colors">
              <ChevronLeft className="h-4 w-4" /> Autre compétence
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- QUIZ ----------
  const q = questions[idx];
  const total = questions.length;
  const progress = Math.round(((idx + (picked !== null ? 1 : 0)) / total) * 100);
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
      <div className="flex items-center justify-between gap-3">
        <button onClick={backToList} data-testid="quiz-quit-btn" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-slate-600">
          <ChevronLeft className="h-4 w-4" /> Quitter
        </button>
        <span className="inline-flex items-center justify-center h-8 px-3 rounded-full bg-sky-100 text-sky-600 text-xs font-extrabold">{topic?.short}</span>
      </div>

      <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-sky-500 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs font-bold text-slate-400 mt-2">Question {idx + 1} / {total}</p>

      <div className="mt-4 rounded-3xl bg-white border border-slate-100 shadow-sm p-6 sm:p-7">
        <h2 className="font-display text-xl font-semibold text-slate-900" data-testid="quiz-question">{q.question}</h2>

        <div className="space-y-2.5 mt-5">
          {q.choices.map((c, i) => {
            const isAnswer = i === q.answer_index;
            const isPicked = picked === i;
            let cls = "border-slate-200 hover:border-sky-300 hover:bg-sky-50/40";
            if (picked !== null) {
              if (isAnswer) cls = "border-emerald-400 bg-emerald-50";
              else if (isPicked) cls = "border-rose-300 bg-rose-50";
              else cls = "border-slate-200 opacity-60";
            }
            return (
              <button key={`${q.id}-${i}`} onClick={() => choose(i)} disabled={picked !== null}
                data-testid={`quiz-choice-${i}`}
                className={`w-full flex items-center gap-3 text-left rounded-2xl border-2 px-4 py-3.5 font-medium text-slate-700 transition-all ${cls}`}>
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-slate-100 text-slate-500 font-bold text-sm shrink-0">{String.fromCharCode(65 + i)}</span>
                <span className="flex-1">{c}</span>
                {picked !== null && isAnswer && <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />}
                {picked !== null && isPicked && !isAnswer && <XCircle className="h-5 w-5 text-rose-400 shrink-0" />}
              </button>
            );
          })}
        </div>

        {picked !== null && (
          <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid="quiz-explanation">
            <p className={`text-sm font-bold ${picked === q.answer_index ? "text-emerald-600" : "text-rose-500"}`}>
              {picked === q.answer_index ? "Bonne réponse !" : "Pas tout à fait…"}
            </p>
            <p className="text-sm text-slate-600 font-medium mt-1 flex items-start gap-1.5"><Sparkles className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" /> {q.explanation}</p>
            <button onClick={next} data-testid="quiz-next-btn"
              className="inline-flex items-center justify-center gap-2 mt-4 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-2.5 font-bold transition-colors">
              {idx + 1 >= total ? "Voir mon score" : "Question suivante"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
